/* ================================================================== *
 * Consultant territories — the single source of truth for the Territories
 * tracker. Each territory maps a set of US states to a consultant under a
 * division head, with the launch baseline k and the opportunity capacity caps.
 *
 * "k" = referral rate = Family/Friend closed customers (trailing 12mo) /
 * total closed customers (prior 12mo) — the same viral coefficient as the
 * company-wide 0.47. Baselines were measured 2026-08 at 90% located coverage.
 * ================================================================== */

export const OPP_CAP = 80; // per-consultant monthly opportunity target (hospitality)
export const OPP_STRETCH = 100; // stretch ceiling → actively hiring past this

export type Division = "East" | "West";

// Product segment filter. Baselines below are frozen per product (measured at
// launch); engagement rings refer far more than the blended all-products number.
export type ProductKey = "all" | "engagement" | "wedding";
export const PRODUCT_OPTIONS: { key: ProductKey; label: string; productType: string | null }[] = [
  { key: "all", label: "All products", productType: null },
  { key: "engagement", label: "Engagement rings", productType: "Engagement Ring" },
  { key: "wedding", label: "Wedding rings", productType: "Wedding Ring" },
];

interface Territory {
  key: string;
  label: string;
  consultant: string;
  division: Division;
  divisionHead: string;
  states: string[]; // canonical full US state names
  baseline: Record<ProductKey, number>; // k at launch per product (frozen Δk reference)
  note?: string;
}

export const DIVISION_HEAD: Record<Division, string> = {
  East: "Jimmy Lacy",
  West: "Zack Starbeck",
};

export const TERRITORIES: Territory[] = [
  {
    key: "east-kentucky",
    label: "Kentucky",
    consultant: "Jake Taylor",
    division: "East",
    divisionHead: "Jimmy Lacy",
    states: ["Kentucky"],
    baseline: { all: 0.56, engagement: 1.08, wedding: 0.24 },
    note: "Largest book; Jimmy assists with overflow.",
  },
  {
    key: "east-gulf",
    label: "Gulf South",
    consultant: "Jim Braden",
    division: "East",
    divisionHead: "Jimmy Lacy",
    states: ["Georgia", "Florida", "Alabama", "Mississippi"],
    baseline: { all: 0.54, engagement: 0.87, wedding: 0.2 },
    note: "Highest referral upside in the East.",
  },
  {
    key: "east-carolinas",
    label: "Carolinas & Mid-Atlantic",
    consultant: "Raleigh Keegan",
    division: "East",
    divisionHead: "Jimmy Lacy",
    states: [
      "South Carolina",
      "North Carolina",
      "Virginia",
      "Tennessee",
      "West Virginia",
      "Maryland",
      "District of Columbia",
    ],
    baseline: { all: 0.75, engagement: 1.26, wedding: 0.29 },
  },
  {
    key: "east-midwest",
    label: "Midwest & Northeast",
    consultant: "Landon Deaton",
    division: "East",
    divisionHead: "Jimmy Lacy",
    states: [
      "Ohio",
      "Illinois",
      "Indiana",
      "Michigan",
      "Wisconsin",
      "Pennsylvania",
      "New York",
      "New Jersey",
      "Massachusetts",
      "Connecticut",
      "Rhode Island",
      "New Hampshire",
      "Delaware",
      "Maine",
      "Vermont",
    ],
    baseline: { all: 0.69, engagement: 1.05, wedding: 0.27 },
    note: "Over capacity — candidate to split into Midwest + Northeast.",
  },
  {
    key: "west-texas",
    label: "South-Central & Plains",
    consultant: "Zack Starbeck",
    division: "West",
    divisionHead: "Zack Starbeck",
    states: [
      "Texas",
      "Oklahoma",
      "Kansas",
      "Nebraska",
      "North Dakota",
      "South Dakota",
      "Missouri",
      "Iowa",
      "Arkansas",
      "Louisiana",
    ],
    baseline: { all: 0.75, engagement: 1.05, wedding: 0.41 },
    note: "Zack runs this while also leading the division.",
  },
  {
    key: "west-pacific",
    label: "Pacific & Mountain",
    consultant: "Chase Megyeri",
    division: "West",
    divisionHead: "Zack Starbeck",
    states: [
      "California",
      "Washington",
      "Oregon",
      "Nevada",
      "Arizona",
      "Utah",
      "Idaho",
      "Montana",
      "Colorado",
      "New Mexico",
      "Wyoming",
      "Hawaii",
      "Alaska",
      "Minnesota",
    ],
    baseline: { all: 0.59, engagement: 0.95, wedding: 0.21 },
    note: "Highest upside in the West — California is the #1 opportunity.",
  },
];

// state (canonical) -> territory key
export const STATE_TO_TERRITORY: Record<string, string> = {};
for (const t of TERRITORIES) for (const s of t.states) STATE_TO_TERRITORY[s] = t.key;

/* ------- US state normalization (abbrev or full name -> full name) ------- */
const FULL = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
  "District of Columbia",
];
const ABBR: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
};
const LOOKUP: Record<string, string> = {};
for (const n of FULL) LOOKUP[n.toLowerCase()] = n;
for (const [a, n] of Object.entries(ABBR)) LOOKUP[a.toLowerCase()] = n;

/** Normalize a raw state value (full name or 2-letter) to canonical full name, or null. */
export function normalizeState(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return LOOKUP[raw.trim().toLowerCase()] ?? null;
}

/** Who is allowed to see the Territories dashboard (by email), besides admins. */
export const TERRITORY_VIEWER_EMAILS = new Set<string>([
  "awillard@faithfulplatform.com",
  "jpolson@faithfulplatform.com",
  "nspalding@faithfulplatform.com",
]);
